import { Menu, Sun, Moon } from 'lucide-react';
import AnalogClock from './AnalogClock.js';
import DigitalClock from './DigitalClock.js';
import useOnlineStatus from './useOnlineStatus.js';
import useTheme from './useTheme.js';

export default function Header({ title, onToggleSidebar, axiosInstance, onSwitchToClassic }) {
  const isOnline = useOnlineStatus(axiosInstance);
  const { isLight, toggleTheme } = useTheme();

  return (
    <header className="theme-bar h-16 flex items-center justify-between px-6 border-b" style={{ borderColor: 'var(--nad-b1)' }}>
      <div className="flex items-center gap-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg transition-all lg:hidden"
          style={{ color: 'var(--nad-t3)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--nad-t1)';
            e.currentTarget.style.background = 'var(--nad-s1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--nad-t3)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-3">
          <h2
            key={title}
            className="text-lg font-bold tracking-tight page-title-anim"
            style={{ color: 'var(--nad-t1)' }}
          >
            {title}
          </h2>
          {onSwitchToClassic && (
            <button
              onClick={onSwitchToClassic}
              className="text-[10px] font-medium px-2.5 py-1 rounded-md border transition-all hover:opacity-80"
              style={{
                borderColor: 'var(--nad-b2)',
                color: 'var(--nad-accent-txt)',
                background: 'var(--nad-s1)',
              }}
            >
              ← Classic
            </button>
          )}
        </div>
      </div>

      <div className="hidden md:flex items-center gap-4">
        <AnalogClock axiosInstance={axiosInstance} />
        <div className="w-px h-8" style={{ background: 'var(--nad-b2)' }} />
        <DigitalClock axiosInstance={axiosInstance} />
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={toggleTheme}
          title={isLight ? 'Switch to dark theme' : 'Switch to light theme'}
          aria-label={isLight ? 'Switch to dark theme' : 'Switch to light theme'}
          className="relative p-2 rounded-lg transition-all duration-300 group"
          style={{ color: 'var(--nad-t3)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--nad-accent-txt)';
            e.currentTarget.style.background = 'var(--nad-s2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--nad-t3)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <span className="block">
            {isLight ? (
              <Moon key="moon" size={18} className="icon-swap" />
            ) : (
              <Sun key="sun" size={18} className="icon-swap" />
            )}
          </span>
          <span
            className="pointer-events-none absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{ boxShadow: '0 0 14px 2px rgba(250,204,21,0.18)' }}
          />
        </button>

        <div className="w-px h-8" style={{ background: 'var(--nad-b2)' }} />

        <div
          className={`flex items-center gap-2.5 px-3 py-1.5 rounded-full border transition-colors duration-500`}
          style={{
            background: isOnline ? 'var(--nad-s1)' : 'rgba(248,113,113,0.08)',
            borderColor: isOnline ? 'var(--nad-b1)' : 'rgba(248,113,113,0.20)',
          }}
        >
          <div className="relative flex items-center justify-center w-4 h-4">
            {isOnline ? (
              <>
                <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping-slow" />
                <span
                  className="absolute inset-[3px] rounded-full bg-emerald-400/20 animate-ping-slow"
                  style={{ animationDelay: '0.4s' }}
                />
                <span
                  className="relative w-1.5 h-1.5 rounded-full animate-ping-pulse"
                  style={{
                    background: 'var(--nad-pos)',
                    boxShadow: '0 0 6px 2px rgba(52,211,153,0.5)',
                  }}
                />
              </>
            ) : (
              <>
                <span className="absolute inset-0 rounded-full bg-red-400/40 animate-ping-slow" />
                <span
                  className="absolute inset-[3px] rounded-full bg-red-400/20 animate-ping-slow"
                  style={{ animationDelay: '0.4s' }}
                />
                <span
                  className="relative w-1.5 h-1.5 rounded-full animate-ping-pulse"
                  style={{
                    background: 'var(--nad-neg)',
                    boxShadow: '0 0 6px 2px rgba(248,113,113,0.5)',
                  }}
                />
              </>
            )}
          </div>
          <span
            className="text-xs font-semibold tracking-wide transition-colors duration-500"
            style={{ color: isOnline ? 'var(--nad-pos)' : 'var(--nad-neg)' }}
          >
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>
    </header>
  );
}
